/* eslint-disable indent */
import { computed, ref, ComputedRef, reactive } from 'vue'
import { Collection, CollectionParams } from '@/types'
import { Advice } from './useAdvices'
import { InvoiceStatus } from './useInvoiceStatuses'
import { useApi } from './useApi'
import { useFiles } from './useFiles'
export type InvoiceAttachment = {
    filename: string
    url: string
    id: number
}

export type InvoiceCorrection = {
    amount: number | null
    carrier: string
    createdAt: string
    description: string
    fileName: string
    id: number
    number: string
    status: InvoiceStatus
    transportRate: number | null
    filenamePdf: string
    attachments: [{ filename: string; id: number }]
}

export type Invoice = {
    advices: Advice[]
    amount: number | null
    carrier: string
    createdAt: string
    description: string
    filename: string | null
    filenamePdf: string | null
    id: number
    number: string
    status: InvoiceStatus
    transportRate: number | null
    attachments: InvoiceAttachment[]
    correction: InvoiceCorrection
}

export type InvoiceListFilters = {
    status?: string | number
}

const loading = ref<boolean>(false)
const invoices = reactive<Collection<Invoice>>({} as Collection<Invoice>)
const isEmpty = computed(() => invoices.items?.length === 0)

export const useInvoices = (): {
    fetchInvoices: (params?: CollectionParams<InvoiceListFilters>) => Promise<void>
    deleteInvoice: (invoiceId: number) => Promise<void>
    downloadInvoice: (invoiceId: number, filename: string | null) => Promise<void>
    downloadAttachment: (attachmentId: number, filename: string | null) => Promise<void>
    invoices: ComputedRef<Collection<Invoice>>
    loading: ComputedRef<boolean>
    isDownloadingInvoiceFile: ComputedRef<boolean>
    isEmpty: ComputedRef<boolean>
} => {
    const { useGet, useDelete } = useApi()
    const fetchInvoices = async (params?: CollectionParams<InvoiceListFilters>) => {
        loading.value = true
        try {
            const _params_ = params
                ? {
                      page: params.page,
                      ...(params?.filters && { ...params?.filters }),
                  }
                : { page: 1 }

            const { status, data } = await useGet('/invoices', { params: _params_ })
            if (status === 200 && data.data && data.data.items) {
                invoices.items = data.data.items
                invoices.metadata = data.data.metadata
            }
        } finally {
            loading.value = false
        }
    }

    const deleteInvoice = async (invoiceId: number) => {
        await useDelete(`/invoices/${invoiceId}`)
    }

    const isDownloadingInvoiceFile = ref<boolean>(false)
    const downloadInvoice = async (invoiceId: number, filename: string | null) => {
        const { getFile } = useFiles()
        isDownloadingInvoiceFile.value = true
        try {
            await getFile(`/invoices/pdf/${invoiceId}`, 'pdf', filename || 'invoice')
        } finally {
            isDownloadingInvoiceFile.value = false
        }
    }

    const downloadAttachment = (attachmentId: number, filename: string | null) => {
        const { getFile } = useFiles()
        return getFile(`/invoices/attachment-file/${attachmentId}`, '', filename || 'attachment')
    }

    return {
        fetchInvoices,
        deleteInvoice,
        downloadInvoice,
        downloadAttachment,
        invoices: computed(() => invoices),
        loading: computed(() => loading.value),
        isDownloadingInvoiceFile: computed(() => isDownloadingInvoiceFile.value),
        isEmpty,
    }
}
